// Burst view: one strip for a run of back-to-back triggers on the same net
// (usually the same fish), so what happened can be read in one place.
//
// Capture windows are drawn to scale with every frame at its own timestamp;
// the dead time between them collapses into hatched breaks that show how long
// it was. A spotlight above the strip shows the colour and depth frame nearest
// the cursor, with the weight and raw cell readings at that moment.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Captures, CaptureSource, Devices, DeviceEventType, TriageResult } from '../api'
import type { CaptureDetail, CaptureLabelSchema, CaptureSummary, DeviceEventSummary } from '../api'
import { groupByCategory } from '../lib/labelCategories'
import { parseWeightTrajectory } from '../lib/weightDebug'
import {
  BURST_CONTEXT_SECONDS,
  burstAround,
  fmtCounts,
  fmtGap,
  fmtGapShort,
  makeLayout,
  mergeWindows,
  niceTicks,
  segX,
  stepTime,
  tAt,
  tickStep,
  xAt,
} from '../lib/burst'
import type { BurstLayout } from '../lib/burst'

const G_PER_LB = 453.592
const TRIGGER_COLORS: Record<string, string> = { weight: '#2f6f9f', button: '#8a4fa8', api: '#b0602c' }
const TARE_COLOR = '#c0168a'
const LABEL_W = 96
const BREAK_W = 112
const ZOOMS = [20, 30, 40, 55, 70, 90, 110]
const DEFAULT_ZOOM = 40
const LABEL_FULL_W = 175 // room a full "#2 weight · 3.8 lb · 37.2 in" tag needs
const TARE_LOOKBACK_SECONDS = 24 * 3600

interface Frame {
  t: number
  k: number
  color: string | null
  depth: string | null
  capture: number // index into captures
  chosen: boolean
}

interface Reading {
  t: number
  weight: number // grams
  outlier: boolean
  used: boolean
  raw: Record<string, number>
}

interface BurstCapture {
  id: string
  t: number
  tEnd: number
  source: string
  weightG: number | null
  lengthMm: number | null
  stable: [number, number] | null
  triage: TriageResult
  labels: CaptureLabelSchema[]
  angler: string | null // from this catch's annotation, if it has one
}

interface SparseRead {
  t: number
  weight: number
  raw: Record<string, number>
}

interface Tare {
  t: number
  grams: number
}

// What the people on the boat said about the fish. Sheet imports (e.g. NASS26)
// put it on one capture of the burst: measured_* is the mean of the guides, and
// guide_estimates holds each guide's own free-text numbers ("9", "18-20", "?").
interface GuideReport {
  captures: number[] // indexes into captures that carry this same report
  weightG: number | null
  lengthMm: number | null
  guides: { name: string; lengthIn: string | null; weightLb: string | null }[]
  label: string | null // nickname, e.g. NASS26-3
  angler: string | null
}

interface BurstData {
  hostname: string
  seed: number // index of the capture the burst was opened from
  guideReports: GuideReport[]
  captures: BurstCapture[]
  frames: Frame[]
  readings: Reading[]
  sparse: SparseRead[]
  tares: Tare[] // in the strip plus the last one before it
  channels: string[]
}

const evT = (ev: DeviceEventSummary) => ev.eventTimestamp.getTime() / 1000
const payloadOf = (ev: DeviceEventSummary) => ev.payload as unknown as Record<string, unknown>
const num = (v: unknown): number | null => (typeof v === 'number' ? v : null)
const annStr = (ann: Record<string, unknown> | null | undefined, key: string): string | null => {
  const v = ann?.[key]
  return typeof v === 'string' && v.trim() !== '' ? v.trim() : null
}

async function listEvents(hostname: string, from: number, to: number, types: DeviceEventType[]) {
  const r = await Devices.listDeviceEvents({
    listDeviceEventsRequest: {
      hostname,
      fromTimestamp: new Date(from * 1000),
      toTimestamp: new Date(to * 1000),
      eventTypes: types,
      maxPerType: 0, // unsampled: the strip needs every reading
    },
  })
  return r.events
}

async function loadBurst(hostname: string, seedId: string): Promise<BurstData> {
  const seed = (await Captures.getCaptures({ getCapturesRequest: { captureIds: [seedId] } })).captures[0]
  const seedT = seed?.captureStartTimestamp
  if (!seed || seedT == null) throw new Error(`capture ${seedId} has no start time`)

  if (seed.source !== CaptureSource.Device) throw new Error('manual captures have no burst')

  // Device captures around the seed, discarded ones left out so a discard can't
  // chain two fish together (the seed itself stays, so the view isn't empty).
  // Widen the search if the burst reaches its edge.
  let span = 3600
  let burst: { captureId: string; t: number }[] = []
  for (let attempt = 0; attempt < 3; attempt++) {
    const listed = (await Captures.listCaptures({
      listCapturesRequest: {
        includeDeviceIds: [hostname],
        includeTriageResults: [TriageResult.Keep, TriageResult.Pending],
        includeThumbnails: false,
        startTimestamp: seedT - span,
        endTimestamp: seedT + span,
      },
    })).captures
    const refs = listed
      .filter(c => c.source === CaptureSource.Device && c.captureStartTimestamp != null && c.captureId !== seedId)
      .map(c => ({ captureId: c.captureId, t: c.captureStartTimestamp! }))
    refs.push({ captureId: seedId, t: seedT })
    burst = burstAround(refs, seedId)
    const nearEdge = burst[0].t < seedT - span + 600 || burst[burst.length - 1].t > seedT + span - 600
    if (!nearEdge) break
    span *= 4
  }
  // The trigger source as the device logged it ("api" was stored as none on older captures).
  const triggerEvents = await listEvents(hostname, burst[0].t - 5, burst[burst.length - 1].t + 5, [DeviceEventType.CaptureTriggered])
  const sourceOf = new Map(triggerEvents.map(ev => [String(payloadOf(ev).captureId), String(payloadOf(ev).triggerSource ?? '')]))

  const details = (await Captures.getCaptures({ getCapturesRequest: { captureIds: burst.map(b => b.captureId) } })).captures
  const byId = new Map(details.map(d => [d.captureId, d]))
  const ordered: CaptureDetail[] = burst.map(b => byId.get(b.captureId)).filter((d): d is CaptureDetail => !!d)

  const captures: BurstCapture[] = []
  const guideReports: GuideReport[] = []
  const frames: Frame[] = []
  const readingsByT = new Map<number, Reading>()
  const channelSet = new Set<string>()
  ordered.forEach((d, ci) => {
    // Frame and weight_debug times are as recorded; a clock correction was only
    // applied to the capture's start/end, so shift them to match.
    const shift = d.appliedShiftSeconds ?? 0
    const t = d.captureStartTimestamp ?? 0
    const traj = parseWeightTrajectory(d.weightDebug)
    captures.push({
      id: d.captureId,
      t,
      tEnd: d.captureEndTimestamp ?? t,
      source: sourceOf.get(d.captureId) || d.triggerSource || '?',
      weightG: d.weightGrams ?? null,
      // the device stores 0 (with no error) when it didn't measure a length
      lengthMm: d.lengthMillimeters != null && d.lengthMillimeters > 0 ? d.lengthMillimeters : null,
      stable: traj?.stableStart != null && traj.stableEnd != null ? [traj.stableStart + shift, traj.stableEnd + shift] : null,
      triage: d.triageResult,
      labels: d.labels ?? [],
      angler: annStr(d.annotation as Record<string, unknown> | null | undefined, 'angler'),
    })
    const ann = d.annotation as Record<string, unknown> | null | undefined
    if (ann) {
      const est = ann.guide_estimates
      const guides = est && typeof est === 'object'
        ? Object.entries(est as Record<string, { length_in?: unknown; weight_lb?: unknown }>).map(([name, e]) => ({
            name,
            lengthIn: e?.length_in != null && e.length_in !== '' ? String(e.length_in) : null,
            weightLb: e?.weight_lb != null && e.weight_lb !== '' ? String(e.weight_lb) : null,
          }))
        : []
      // raw snake_case keys, as the side panel reads them (the client keeps them)
      const weightG = num(ann.measured_weight_grams) ?? num(ann.measuredWeightGrams)
      const lengthMm = num(ann.measured_length_mm) ?? num(ann.measuredLengthMm)
      if (weightG != null || lengthMm != null || guides.length > 0) {
        // an import may tag every trigger of the fish with the same report: keep one
        const label = annStr(ann, 'nickname')
        const angler = annStr(ann, 'angler')
        const same = guideReports.find(g => JSON.stringify([g.weightG, g.lengthMm, g.guides, g.label, g.angler]) === JSON.stringify([weightG, lengthMm, guides, label, angler]))
        if (same) same.captures.push(ci)
        else guideReports.push({ captures: [ci], weightG, lengthMm, guides, label, angler })
      }
    }
    const urlFor = (suffix: string) => d.images.find(i => i.s3Key.endsWith(suffix) && i.url)?.url ?? null
    for (const f of d.frames ?? []) {
      const nn = String(f.frameIndex).padStart(2, '0')
      frames.push({
        t: f.captureTimestamp + shift,
        k: f.frameIndex,
        color: urlFor(`raw/realsense_color_${nn}.jpg`),
        depth: urlFor(`raw/realsense_depth_vis_${nn}.jpg`),
        capture: ci,
        chosen: f.frameIndex === d.chosenFrameIndex,
      })
    }
    // Back-to-back captures share readings (each blob starts ~3 s before its
    // trigger): merge by timestamp, and a reading counts as used if any capture used it.
    for (const r of traj?.readings ?? []) {
      const key = Math.round((r.t + shift) * 1000)
      const raw: Record<string, number> = {}
      for (const [ch, c] of Object.entries(r.cells)) { raw[ch] = c.raw; channelSet.add(ch) }
      const prev = readingsByT.get(key)
      if (prev) prev.used = prev.used || r.used
      else readingsByT.set(key, { t: r.t + shift, weight: r.weight, outlier: r.outlier, used: r.used, raw })
    }
  })
  if (captures.length === 0) throw new Error('no capture details came back')

  const from = captures[0].t - BURST_CONTEXT_SECONDS
  const to = captures[captures.length - 1].tEnd + BURST_CONTEXT_SECONDS
  const [inStrip, before] = await Promise.all([
    listEvents(hostname, from, to, [DeviceEventType.WeightRead, DeviceEventType.Tare]),
    listEvents(hostname, from - TARE_LOOKBACK_SECONDS, from, [DeviceEventType.Tare]),
  ])
  const sparse: SparseRead[] = []
  const tares: Tare[] = []
  for (const ev of [...before.slice(-1), ...inStrip]) {
    const p = payloadOf(ev)
    if (ev.eventType === DeviceEventType.Tare) {
      tares.push({ t: evT(ev), grams: num(p.tareGrams) ?? 0 })
    } else if (ev.eventType === DeviceEventType.WeightRead) {
      const raw: Record<string, number> = {}
      ;((p.channelReadings as Array<{ rawValue?: number }> | undefined) ?? []).forEach((c, i) => {
        if (typeof c?.rawValue === 'number') raw[String(i)] = c.rawValue
      })
      sparse.push({ t: evT(ev), weight: num(p.weightGrams) ?? 0, raw })
    }
  }
  tares.sort((a, b) => a.t - b.t)

  return {
    hostname,
    seed: Math.max(0, captures.findIndex(c => c.id === seedId)),
    guideReports,
    captures,
    frames: frames.sort((a, b) => a.t - b.t),
    readings: Array.from(readingsByT.values()).sort((a, b) => a.t - b.t),
    sparse: sparse.sort((a, b) => a.t - b.t),
    tares,
    channels: Array.from(channelSet).sort(),
  }
}

/**
 * The nearest kept or untriaged device capture on this net after `fromT`
 * (dir 1) or before it (dir -1). /capture/list returns the newest 250 in a
 * window, so looking forward narrows the window until nothing is cut off.
 */
async function findNeighbor(hostname: string, fromT: number, dir: 1 | -1): Promise<{ captureId: string; t: number } | null> {
  for (const span of [6 * 3600, 2 * 86400, 30 * 86400]) {
    let lo = dir > 0 ? fromT + 0.001 : fromT - span
    let hi = dir > 0 ? fromT + span : fromT - 0.001
    let best: { captureId: string; t: number } | null = null
    for (let i = 0; i < 10; i++) {
      const r = await Captures.listCaptures({
        listCapturesRequest: {
          includeDeviceIds: [hostname],
          includeTriageResults: [TriageResult.Keep, TriageResult.Pending],
          includeThumbnails: false,
          startTimestamp: lo,
          endTimestamp: hi,
        },
      })
      for (const c of r.captures) {
        const t = c.captureStartTimestamp
        if (c.source !== CaptureSource.Device || t == null) continue
        if (dir > 0 ? t > fromT && (!best || t < best.t) : t < fromT && (!best || t > best.t)) best = { captureId: c.captureId, t }
      }
      // newest-first: looking back, the first page already holds the nearest one
      if (!r.truncated || dir < 0) break
      const oldest = Math.min(...r.captures.map(c => c.captureStartTimestamp ?? Infinity))
      if (!Number.isFinite(oldest) || oldest <= lo) break
      hi = oldest
    }
    if (best) return best
  }
  return null
}

interface Props {
  hostname: string
  captureId: string
  // Open the burst around another capture (previous / next burst on this net).
  onOpenBurst: (captureId: string) => void
  // The page's capture list: triage and label edits made in the side panel land
  // here first, so the burst reads them from it rather than from its own fetch.
  captureSummaries: CaptureSummary[]
  units: 'g' | 'lb'
  onUnits: (u: 'g' | 'lb') => void
  fmtClock: (ms: number) => string
  tzLabel: string
  onExit: (range: { start: number; end: number } | null) => void
  onOpenCatch: (captureId: string) => void
  // A catch's labels changed here; the page updates its capture list (and so the side panel).
  onLabelsChange: (captureId: string, labels: CaptureLabelSchema[]) => void
}

const TRIAGE_STYLE: Record<string, { label: string; cls: string; mark: string; color: string }> = {
  keep: { label: 'Keep', cls: 'bg-pine text-white', mark: '✓', color: '#355E3B' },
  pending: { label: 'Untriaged', cls: 'bg-amber-400 text-amber-900', mark: '?', color: '#b45309' },
  discard: { label: 'Discard', cls: 'bg-coral text-white', mark: '✕', color: '#D8613C' },
}

export default function BurstView({ hostname, captureId, onOpenBurst, captureSummaries, units, onUnits, fmtClock, tzLabel, onExit, onOpenCatch, onLabelsChange }: Props) {
  const [data, setData] = useState<BurstData | null>(null)
  const edits = useMemo(() => new Map(captureSummaries.map(c => [c.captureId, c])), [captureSummaries])
  const [error, setError] = useState<string | null>(null)
  const [zoom, setZoom] = useState<number>(() => {
    const v = Number(window.localStorage.getItem('kodiak-burst-zoom'))
    return ZOOMS.includes(v) ? v : DEFAULT_ZOOM
  })
  useEffect(() => { window.localStorage.setItem('kodiak-burst-zoom', String(zoom)) }, [zoom])

  useEffect(() => {
    let cancelled = false
    setData(null)
    setError(null)
    loadBurst(hostname, captureId)
      .then(d => { if (!cancelled) setData(d) })
      .catch((e: Error) => { if (!cancelled) setError(e.message) })
    return () => { cancelled = true }
  }, [hostname, captureId])

  // The bursts either side on this net: looked up once the burst is loaded.
  const [neighbors, setNeighbors] = useState<{ prev: { captureId: string; t: number } | null; next: { captureId: string; t: number } | null } | null>(null)
  useEffect(() => {
    setNeighbors(null)
    if (!data) return
    let cancelled = false
    const first = data.captures[0].t
    const last = data.captures[data.captures.length - 1].t
    Promise.all([findNeighbor(hostname, first, -1), findNeighbor(hostname, last, 1)])
      .then(([prev, next]) => { if (!cancelled) setNeighbors({ prev, next }) })
      .catch(() => { if (!cancelled) setNeighbors({ prev: null, next: null }) })
    return () => { cancelled = true }
  }, [data, hostname])

  // [ and ] step to the previous / next burst
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) return
      const target = e.key === '[' ? neighbors?.prev : e.key === ']' ? neighbors?.next : null
      if (target) { e.preventDefault(); onOpenBurst(target.captureId) }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [neighbors, onOpenBurst])

  const range = data
    ? { start: (data.captures[0].t - BURST_CONTEXT_SECONDS) * 1000, end: (data.captures[data.captures.length - 1].tEnd + BURST_CONTEXT_SECONDS) * 1000 }
    : null

  return (
    <div className="flex-1 flex flex-col min-h-0 bg-white overflow-y-auto">
      <div className="flex items-center gap-3 px-5 py-2 border-b border-stone-200 text-[11px]">
        <button
          onClick={() => onExit(range)}
          className="font-mono text-[10px] uppercase tracking-[0.18em] text-bear hover:text-bear-dark font-semibold"
        >
          ← timeline
        </button>
        <span className="text-stone-300">|</span>
        <span className="font-display font-extrabold uppercase tracking-[0.18em] text-ink-700">Burst</span>
        {data && (
          <span className="text-stone-500">
            {data.captures.length} trigger{data.captures.length === 1 ? '' : 's'} ·{' '}
            {fmtClock(data.captures[0].t * 1000)}–{fmtClock(data.captures[data.captures.length - 1].tEnd * 1000)} {tzLabel} ·{' '}
            {fmtGap(data.captures[data.captures.length - 1].tEnd - data.captures[0].t)}
          </span>
        )}
        {data && (['prev', 'next'] as const).map(which => {
          const n = neighbors?.[which]
          const ref = which === 'prev' ? data.captures[0].t : data.captures[data.captures.length - 1].t
          const away = n ? fmtGap(Math.abs(n.t - ref)) : null
          return (
            <button key={which} disabled={!n} onClick={() => n && onOpenBurst(n.captureId)}
              title={!neighbors ? 'looking…' : n
                ? `${which === 'prev' ? 'previous' : 'next'} burst on this net: ${fmtClock(n.t * 1000)} ${tzLabel}, ${away} ${which === 'prev' ? 'before' : 'after'} (${which === 'prev' ? '[' : ']'})`
                : `no ${which === 'prev' ? 'earlier' : 'later'} kept catch on this net`}
              className="px-2 h-6 rounded border border-stone-200 font-mono text-[10px] uppercase tracking-[0.14em] text-stone-600 hover:border-bear/40 hover:text-bear disabled:opacity-35 disabled:hover:border-stone-200 disabled:hover:text-stone-600">
              {which === 'prev' ? '‹ previous' : 'next ›'}
            </button>
          )
        })}
        <span className="flex-1" />
        {(['g', 'lb'] as const).map(u => (
          <button key={u} onClick={() => onUnits(u)}
            className={`px-2 h-6 rounded border text-[10.5px] ${units === u ? 'bg-bear/5 text-bear-dark border-bear/30' : 'border-stone-200 text-stone-500 hover:border-bear/40'}`}>
            {u}
          </button>
        ))}
        <span className="w-3" />
        <span className="text-stone-400 uppercase tracking-[0.14em] text-[10px]">zoom</span>
        <button className="px-2 h-6 border border-stone-200 rounded hover:border-bear/40" title="zoom out"
          onClick={() => setZoom(z => ZOOMS[Math.max(0, ZOOMS.indexOf(z) - 1)])}>−</button>
        <span className="tabular-nums w-14 text-center text-stone-500">{zoom} px/s</span>
        <button className="px-2 h-6 border border-stone-200 rounded hover:border-bear/40" title="zoom in"
          onClick={() => setZoom(z => ZOOMS[Math.min(ZOOMS.length - 1, ZOOMS.indexOf(z) + 1)])}>+</button>
      </div>
      {error && <div className="px-5 py-4 text-[12px] text-bear">Couldn't load the burst: {error}</div>}
      {!data && !error && <div className="px-5 py-4 text-[12px] text-stone-500">loading burst…</div>}
      {data && (
        <BurstStrip data={data} edits={edits} zoom={zoom} units={units} fmtClock={fmtClock} onOpenCatch={onOpenCatch} onLabelsChange={onLabelsChange} />
      )}
    </div>
  )
}

// ------------------------------------------------------------
// Strip + spotlight
// ------------------------------------------------------------

type LaneName = 'frames' | 'events' | 'weight' | `raw:${string}` | 'time'

interface LaneScale {
  y: (v: number) => number // clamped to the lane
  clip: (v: number) => number // 1 above the lane's range, -1 below, 0 inside
  ticks: [number, string][]
}

// The load-cell ADC is 24-bit: readings at its limits mean the input is pinned
// (overloaded or disconnected), not a real load.
const isRailed = (v: number | null | undefined) => v != null && Math.abs(v) >= 8_388_000

function BurstStrip({ data, edits, zoom, units, fmtClock, onOpenCatch, onLabelsChange }: {
  data: BurstData
  edits: Map<string, CaptureSummary>
  zoom: number
  units: 'g' | 'lb'
  fmtClock: (ms: number) => string
  onOpenCatch: (captureId: string) => void
  onLabelsChange: (captureId: string, labels: CaptureLabelSchema[]) => void
}) {
  const { captures, frames, readings, sparse, tares, channels } = data
  // triage and labels as the page has them (side-panel edits included)
  const triageOf = (c: BurstCapture) => edits.get(c.id)?.triageResult ?? c.triage
  const [labelEdits, setLabelEdits] = useState<Record<string, CaptureLabelSchema[]>>({})
  const labelsOf = (c: BurstCapture) => labelEdits[c.id] ?? edits.get(c.id)?.labels ?? c.labels
  const setLabels = (captureId: string, labels: CaptureLabelSchema[]) => {
    setLabelEdits(prev => ({ ...prev, [captureId]: labels }))
    onLabelsChange(captureId, labels)
  }
  // every catch label an admin has defined, for the toggles in the catch box
  const [catchOptions, setCatchOptions] = useState<CaptureLabelSchema[] | null>(null)
  useEffect(() => {
    Captures.listCaptureLabels({ body: {} })
      .then(r => setCatchOptions(r.labels.filter(l => l.kind === 'catch')))
      .catch(() => setCatchOptions([]))
  }, [])
  const leadT0 = captures[0].t - BURST_CONTEXT_SECONDS
  const tailT1 = captures[captures.length - 1].tEnd + BURST_CONTEXT_SECONDS

  const layout: BurstLayout = useMemo(() => {
    const windows: [number, number][] = captures.map((c, i) => {
      const ts = [c.t, c.tEnd]
      for (const f of frames) if (f.capture === i) ts.push(f.t)
      return [Math.min(...ts) - 0.3, Math.max(...ts) + 0.6]
    })
    // dense readings extend each window (they start ~3 s before the trigger)
    for (const r of readings) {
      const w = windows.find(w => r.t >= w[0] - 4 && r.t <= w[1] + 4)
      if (w) { w[0] = Math.min(w[0], r.t - 0.3); w[1] = Math.max(w[1], r.t + 0.6) }
    }
    return makeLayout(mergeWindows(windows), leadT0, tailT1, zoom, BREAK_W)
  }, [captures, frames, readings, leadT0, tailT1, zoom])

  const frameW = Math.max(6, Math.floor(zoom * 0.38))
  const frameH = Math.floor((frameW * 3) / 4)
  const lanes: [LaneName, number][] = [
    ['frames', frameH + 6],
    ['events', 40],
    ['weight', 120],
    ...channels.map(ch => [`raw:${ch}` as LaneName, 76] as [LaneName, number]),
    ['time', 18],
  ]
  const laneY: Record<string, [number, number]> = {}
  let yAcc = 0
  for (const [name, h] of lanes) { laneY[name] = [yAcc, h]; yAcc += h }
  const totalH = yAcc
  const bottom = laneY.time[0]
  const toUnits = (g: number) => (units === 'lb' ? g / G_PER_LB : g)
  const fmtW = (g: number) => (units === 'lb' ? `${(g / G_PER_LB).toFixed(1)} lb` : `${Math.round(g)} g`)

  // Lane scales, shared across the whole burst so triggers stay comparable.
  // Each lane fits the middle 96% of its readings, so one railed ADC reading
  // (e.g. 2^24 counts, which the device turned into 1066 lb) can't flatten the
  // rest. Readings outside are pinned to the lane edge (`clip` says which edge).
  const scales = useMemo(() => {
    const out: Record<string, LaneScale> = {}
    const mk = (lane: string, vals: number[], fmt: (v: number) => string, includeZero = false) => {
      const [y0, h] = laneY[lane]
      const pad = 6
      const sorted = vals.filter(v => Number.isFinite(v)).sort((a, b) => a - b)
      const q = (f: number) => sorted.length ? sorted[Math.min(sorted.length - 1, Math.max(0, Math.round(f * (sorted.length - 1))))] : 0
      let lo = q(0.02)
      let hi = q(0.98)
      if (includeZero) { lo = Math.min(lo, 0); hi = Math.max(hi, 0) }
      const span = hi - lo > 1e-9 ? hi - lo : Math.max(Math.abs(hi), 1)
      lo -= span * 0.08
      hi += span * 0.08
      const clip = (v: number) => (v > hi ? 1 : v < lo ? -1 : 0)
      const y = (v: number) => y0 + pad + (1 - (Math.min(hi, Math.max(lo, v)) - lo) / (hi - lo)) * (h - 2 * pad)
      out[lane] = { y, clip, ticks: niceTicks(lo, hi, Math.max(3, Math.floor(h / 18))).map(v => [y(v), fmt(v)]) }
    }
    // readings with a railed channel don't get a say in the scale at all
    const sane = <T extends { raw: Record<string, number> }>(arr: T[]) => arr.filter(r => !Object.values(r.raw).some(isRailed))
    const inSeg = sane(sparse.filter(p => segX(layout, p.t) != null))
    mk('weight', [...sane(readings).map(r => toUnits(r.weight)), ...inSeg.map(p => toUnits(p.weight))], v => `${+v.toFixed(2)} ${units}`, true)
    for (const ch of channels) {
      mk(`raw:${ch}`, [...readings.map(r => r.raw[ch]), ...sparse.map(p => p.raw[ch])].filter(v => v != null && !isRailed(v)), fmtCounts)
    }
    return out
    // laneY is derived from zoom/channels, both in the deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readings, sparse, channels, layout, units, zoom])

  const wy = (g: number) => scales.weight.y(toUnits(g))
  const wclip = (g: number) => scales.weight.clip(toUnits(g))
  // An off-scale reading: a small triangle on the lane edge pointing the way it went.
  const edgeMark = (key: string, px: number, py: number, dir: number, color: string, title: string) => (
    <path key={key} d={dir > 0 ? `M${px - 4},${py + 4}L${px},${py - 3}L${px + 4},${py + 4}Z` : `M${px - 4},${py - 4}L${px},${py + 3}L${px + 4},${py - 4}Z`}
      fill={color}><title>{title}</title></path>
  )

  // ---- cursor, spotlight, keyboard --------------------------------------
  // start on the frame the device chose for the catch the burst was opened from
  const chosenStart = frames.find(f => f.capture === data.seed && f.chosen) ?? frames.find(f => f.capture === data.seed) ?? frames[0]
  const [curT, setCurT] = useState<number>(chosenStart?.t ?? captures[0].t)
  const [pinned, setPinned] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)
  const curRef = useRef(curT)
  curRef.current = curT

  const follow = useCallback((t: number) => {
    const sc = scrollRef.current
    const x = xAt(layout, t)
    if (!sc || x == null) return
    const m = sc.clientWidth * 0.2
    if (x < sc.scrollLeft + m || x > sc.scrollLeft + sc.clientWidth - m) sc.scrollLeft = x - sc.clientWidth / 2
  }, [layout])

  // Centre the opening frame once per layout.
  useEffect(() => {
    const sc = scrollRef.current
    const x = xAt(layout, curRef.current)
    if (sc && x != null) sc.scrollLeft = x - sc.clientWidth / 2
  }, [layout])

  // Hold ←/→ to move through time: starts at 3 s/s and ramps to 15 s/s while
  // held; Shift is a slow 1 s/s. Breaks are skipped.
  useEffect(() => {
    let dir = 0
    let slow = false
    let held = 0
    let last: number | null = null
    let raf = 0
    const tick = (now: number) => {
      if (!dir) { last = null; return }
      if (last != null) {
        const dt = (now - last) / 1000
        held += dt
        const speed = slow ? 1 : Math.min(15, 3 + 6 * held)
        const t = stepTime(layout, curRef.current, dir * speed * dt)
        curRef.current = t
        setCurT(t)
        follow(t)
      }
      last = now
      raf = requestAnimationFrame(tick)
    }
    const down = (e: KeyboardEvent) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return
      const el = e.target as HTMLElement | null
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) return
      e.preventDefault()
      slow = e.shiftKey
      setPinned(true)
      const d = e.key === 'ArrowRight' ? 1 : -1
      if (dir !== d) {
        dir = d
        held = 0
        const t = stepTime(layout, curRef.current, d * 0.05)
        curRef.current = t
        setCurT(t)
        follow(t)
        cancelAnimationFrame(raf)
        raf = requestAnimationFrame(tick)
      }
    }
    const up = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') dir = 0
      if (e.key === 'Shift') slow = false
    }
    window.addEventListener('keydown', down)
    window.addEventListener('keyup', up)
    return () => {
      window.removeEventListener('keydown', down)
      window.removeEventListener('keyup', up)
      cancelAnimationFrame(raf)
    }
  }, [layout, follow])

  const tFromEvent = (e: React.MouseEvent<HTMLDivElement>) => tAt(layout, e.clientX - e.currentTarget.getBoundingClientRect().left)
  const onMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (pinned) return
    const t = tFromEvent(e)
    if (t != null) setCurT(t)
  }
  const onClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const t = tFromEvent(e)
    if (t == null) return
    setPinned(p => !p)
    setCurT(t)
  }

  const nearestFrame = useMemo(() => {
    let best: Frame | null = null
    for (const f of frames) if (!best || Math.abs(f.t - curT) < Math.abs(best.t - curT)) best = f
    return best
  }, [frames, curT])
  const nearest = <T extends { t: number }>(arr: T[], tol: number): T | null => {
    let best: T | null = null
    for (const p of arr) if (Math.abs(p.t - curT) < tol && (!best || Math.abs(p.t - curT) < Math.abs(best.t - curT))) best = p
    return best
  }
  const dense = nearest(readings, 0.3)
  const sp = dense ? null : nearest(sparse, 2.6)
  const fc = nearestFrame ? captures[nearestFrame.capture] : null

  // ---- drawing ------------------------------------------------------------
  const svg: React.ReactNode[] = []
  const divs: React.ReactNode[] = []
  const textH = laneY.weight[0] - 2

  for (const lane of ['weight', ...channels.map(c => `raw:${c}`)]) {
    const [y0, h] = laneY[lane]
    svg.push(<rect key={`bg-${lane}`} x={0} y={y0} width={layout.width} height={h} fill="#fafafa" />)
    for (const [ty] of scales[lane].ticks) {
      svg.push(<line key={`g-${lane}-${ty}`} x1={0} x2={layout.width} y1={ty} y2={ty} stroke="#ececec" />)
    }
  }

  // breaks: hatch, the 5 s weight as a tiny line, elapsed time, last tare when it's off-strip
  layout.rows.forEach((r, i) => {
    if (r.kind !== 'break') return
    svg.push(<rect key={`h-${i}`} x={r.x0 + 3} y={1} width={r.x1 - r.x0 - 6} height={totalH - 2} rx={5} fill="url(#burst-hatch)" />)
    const reads = sparse.filter(p => p.t > r.t0 && p.t < r.t1)
    if (reads.length) {
      const pts = reads.map(p => `${xAt(layout, p.t)!.toFixed(1)},${wy(p.weight).toFixed(1)}`).join(' ')
      svg.push(<polyline key={`sp-${i}`} points={pts} fill="none" stroke="#7a6fa0" strokeWidth={1.5} />)
      reads.forEach((p, j) => svg.push(<circle key={`spp-${i}-${j}`} cx={xAt(layout, p.t)!} cy={wy(p.weight)} r={1.5} fill="#7a6fa0" />))
    }
    const lead = i === 0
    const tail = i === layout.rows.length - 1
    const gap = r.t1 - r.t0
    const shade = Math.round(150 - 110 * Math.min(1, Math.log10(Math.max(gap, 1)) / Math.log10(600)))
    const ref = tail ? captures[captures.length - 1].tEnd : (captures.find(c => c.t >= r.t1 - 1e-6)?.t ?? r.t1)
    const refName = tail ? `#${captures.length} end` : `#${captures.findIndex(c => c.t >= r.t1 - 1e-6) + 1}`
    const prior = tares.filter(tr => tr.t <= ref)
    const lastTare = prior[prior.length - 1]
    let tareNote: string | null = null
    if (!lastTare) tareNote = 'no tare in 24 h'
    else if (lastTare.t < leadT0) tareNote = `last tare ${fmtGap(ref - lastTare.t)} before ${refName}`
    const w = r.x1 - r.x0 - 6
    divs.push(
      <div key={`bt-${i}`} className="absolute text-center overflow-hidden" style={{ left: r.x0 + 3, width: w, top: 1, height: textH, paddingTop: 20, zIndex: 7 }}>
        <div className="inline-block rounded px-1.5 font-extrabold leading-tight" style={{ background: '#efece6', fontSize: w > 70 ? 22 : 13, color: `rgb(${shade},${shade - 10},${shade - 25})` }}>
          {w > 70 ? fmtGap(gap) : fmtGapShort(gap)}
        </div>
        <div><span className="inline-block rounded px-1 text-[9.5px] uppercase tracking-[0.1em] text-stone-500" style={{ background: '#efece6' }}>
          {lead ? 'before #1' : tail ? `after #${captures.length}` : 'elapsed'}
        </span></div>
        {tareNote && <div className="text-[10.5px] font-semibold leading-tight mt-0.5" style={{ color: TARE_COLOR }}>{tareNote}</div>}
      </div>,
    )
  })

  // captures: stable band, start/end bracket, trigger line and tag
  captures.forEach((c, i) => {
    const xs = segX(layout, c.t)
    const xe = segX(layout, c.tEnd)
    if (xs == null || xe == null) return
    const col = TRIGGER_COLORS[c.source] ?? '#666'
    if (c.stable) {
      const a = segX(layout, c.stable[0])
      const b = segX(layout, c.stable[1])
      if (a != null && b != null) {
        svg.push(<rect key={`st-${i}`} x={a} y={laneY.weight[0]} width={Math.max(1, b - a)} height={laneY.weight[1]} fill="#e3f0dd" />)
      }
    }
    const [ey, eh] = laneY.events
    svg.push(<rect key={`br-${i}`} x={xs} y={ey + eh - 9} width={xe - xs} height={6} fill="#d9d4ca" />)
    svg.push(<line key={`tr-${i}`} x1={xs} x2={xs} y1={0} y2={bottom} stroke={col} strokeWidth={2} />)
    svg.push(<line key={`en-${i}`} x1={xe} x2={xe} y1={ey} y2={bottom} stroke="#999" strokeDasharray="3 2" />)
    const nextX = i + 1 < captures.length ? segX(layout, captures[i + 1].t) ?? Infinity : Infinity
    const full = nextX - xs > LABEL_FULL_W
    const detail = `${c.weightG != null ? fmtW(c.weightG) : '—'} · ${c.lengthMm != null ? `${(c.lengthMm / 25.4).toFixed(1)} in` : '—'}`
    divs.push(
      <button key={`tag-${i}`} onClick={(e) => { e.stopPropagation(); onOpenCatch(c.id) }}
        title={`#${i + 1} ${c.source} · ${detail} · ${TRIAGE_STYLE[triageOf(c)]?.label ?? triageOf(c)} · open in side panel`}
        className="absolute whitespace-nowrap bg-white/90 px-1 text-[11px] hover:bg-stone-100"
        style={{ left: xs + 2, top: ey + 1, borderLeft: `3px solid ${col}`, zIndex: 6 }}>
        <TriageMark triage={triageOf(c)} />
        <b style={{ color: col }}>#{i + 1}{full ? ` ${c.source}` : ''}</b>{full ? ` · ${detail}` : ''}
      </button>,
    )
  })

  // capture segments: weight readings, raw channels, zero line, time ticks
  const step = tickStep(zoom)
  layout.rows.forEach((r, i) => {
    if (r.kind !== 'seg') return
    const pts = readings.filter(p => p.t >= r.t0 && p.t <= r.t1)
    // The line goes through every reading, flagged ones included: the live
    // filter flags the jump when a fish lands, and skipping them would hide it.
    svg.push(<polyline key={`w-${i}`} fill="none" stroke="#222" strokeWidth={1.3}
      points={pts.map(p => `${segX(layout, p.t)!.toFixed(1)},${wy(p.weight).toFixed(1)}`).join(' ')} />)
    channels.forEach((ch, ci) => {
      const ys = scales[`raw:${ch}`].y
      const col = ci % 2 ? '#b0602c' : '#2f6f9f'
      svg.push(<polyline key={`r-${i}-${ch}`} fill="none" stroke={col} strokeWidth={1.1}
        points={pts.filter(p => p.raw[ch] != null).map(p => `${segX(layout, p.t)!.toFixed(1)},${ys(p.raw[ch]).toFixed(1)}`).join(' ')} />)
      pts.forEach((p, j) => {
        const v = p.raw[ch]
        const off = v != null ? scales[`raw:${ch}`].clip(v) : 0
        if (off) svg.push(edgeMark(`or-${i}-${ch}-${j}`, segX(layout, p.t)!, ys(v), off, col, `off scale: ${v}${isRailed(v) ? ' (ADC railed)' : ''}`))
      })
    })
    pts.forEach((p, j) => {
      const px = segX(layout, p.t)!
      const py = wy(p.weight)
      const off = wclip(p.weight)
      if (off) {
        svg.push(edgeMark(`ow-${i}-${j}`, px, py, off, p.used ? '#2e9d4b' : p.outlier ? '#f08a8a' : '#222',
          `off scale: ${fmtW(p.weight)}${p.used ? ' (used for reported weight)' : ''}`))
      } else if (p.used) {
        svg.push(<circle key={`u-${i}-${j}`} cx={px} cy={py} r={4.4} fill="#2e9d4b" stroke="#fff" strokeWidth={1.2}>
          <title>used for reported weight · {fmtW(p.weight)}</title></circle>)
      } else if (p.outlier) {
        const a = 3.2
        svg.push(<path key={`o-${i}-${j}`} d={`M${px - a},${py - a}L${px + a},${py + a}M${px - a},${py + a}L${px + a},${py - a}`}
          stroke="#f08a8a" strokeWidth={1.6} fill="none" />)
      } else {
        svg.push(<circle key={`p-${i}-${j}`} cx={px} cy={py} r={2.2} fill="#222" />)
      }
    })
    svg.push(<line key={`z-${i}`} x1={r.x0} x2={r.x1} y1={wy(0)} y2={wy(0)} stroke="#ccc" strokeDasharray="2 2" />)
    for (let s = Math.ceil(r.t0 / step) * step; s <= r.t1; s += step) {
      const tx = segX(layout, s)!
      if (tx - r.x0 < 24 || r.x1 - tx < 24) continue
      svg.push(<line key={`tk-${i}-${s}`} x1={tx} x2={tx} y1={bottom} y2={bottom + 4} stroke="#aaa" />)
      divs.push(<div key={`tl-${i}-${s}`} className="absolute text-center text-[10px] text-stone-400 tabular-nums"
        style={{ left: tx - 30, width: 60, top: bottom + 3 }}>{fmtClock(s * 1000)}</div>)
    }
  })

  // tares last so nothing covers them
  tares.forEach((tr, i) => {
    if (tr.t < leadT0 || tr.t > tailT1) return
    const ex = xAt(layout, tr.t)
    if (ex == null) return
    const sign = tr.grams >= 0 ? '+' : ''
    const label = units === 'lb' ? `${sign}${(tr.grams / G_PER_LB).toFixed(2)} lb` : `${sign}${Math.round(tr.grams)} g`
    svg.push(<rect key={`tb-${i}`} x={ex - 5} y={0} width={10} height={bottom} fill={TARE_COLOR} opacity={0.13} />)
    svg.push(<line key={`tl-${i}`} x1={ex} x2={ex} y1={0} y2={bottom} stroke={TARE_COLOR} strokeWidth={3} />)
    divs.push(
      <div key={`tp-${i}`} title={`tare ${label} · ${fmtClock(tr.t * 1000)}`}
        className="absolute whitespace-nowrap rounded-full px-2 text-[11px] font-bold text-white shadow"
        style={{ left: ex, top: 1, transform: 'translateX(-50%)', background: TARE_COLOR, zIndex: 8 }}>
        ⚖ TARE {label}
      </div>,
    )
  })

  // frame thumbnails at their own timestamps
  frames.forEach((f, i) => {
    const fx = segX(layout, f.t)
    if (fx == null || !f.color) return
    const hot = nearestFrame === f
    divs.push(
      <img key={`f-${i}`} src={f.color} alt="" loading="lazy" draggable={false}
        className="absolute object-cover box-border"
        style={{
          left: fx - frameW / 2, top: laneY.frames[0] + 3, width: frameW, height: frameH, zIndex: hot ? 5 : 2,
          border: `2px solid ${f.chosen ? '#e0a000' : '#fff'}`, outline: hot ? '3px solid #e0a000' : undefined,
        }} />,
    )
  })

  const cursorX = xAt(layout, curT)
  const laneLabels: Record<string, string> = { frames: 'frames', events: 'events', weight: 'weight', time: 'time' }

  return (
    <div className="flex flex-col">
      <svg width={0} height={0} className="absolute">
        <defs>
          <pattern id="burst-hatch" width={10} height={10} patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <rect width={10} height={10} fill="#efece6" />
            <rect width={5} height={10} fill="#e7e3db" />
          </pattern>
        </defs>
      </svg>

      {/* spotlight */}
      <div className="flex gap-3 items-start px-5 pt-3 pb-2">
        <SpotImage url={nearestFrame?.color ?? null} chosen={!!nearestFrame?.chosen} />
        <SpotImage url={nearestFrame?.depth ?? null} chosen={!!nearestFrame?.chosen} />
        <div className="font-mono text-[12.5px] leading-relaxed text-ink-700 min-w-0">
          <GuideCard reports={data.guideReports.filter(g => nearestFrame != null && g.captures.includes(nearestFrame.capture))}
            fmtW={fmtW} onOpenCatch={onOpenCatch} captures={captures} />
          {nearestFrame && fc && (
            <InfoBox tone="device"
              title={<>
                <button className="uppercase tracking-[0.16em] font-semibold hover:text-bear" onClick={() => onOpenCatch(fc.id)} title="open in side panel">
                  Catch #{nearestFrame.capture + 1} · {fc.source} trigger
                </button>
                <TriagePill triage={triageOf(fc)} />
              </>}
              big={<>
                <span>{fc.lengthMm != null ? fmtIn(fc.lengthMm) : '—'}</span>
                <span>{fc.weightG != null ? fmtW(fc.weightG) : '—'}</span>
              </>}
              lines={[
                (() => {
                  const angler = fc.angler ?? data.guideReports.find(g => g.captures.includes(nearestFrame.capture))?.angler ?? null
                  return (
                    <span className="text-[13px]">
                      <span className="text-stone-500">angler </span>
                      {angler ? <b className="text-ink-700">{angler}</b> : <span className="text-stone-400">not recorded</span>}
                    </span>
                  )
                })(),
                `frame ${nearestFrame.k}${nearestFrame.chosen ? ' (chosen)' : ''} · ${fmtClock(nearestFrame.t * 1000)} · trigger +${(nearestFrame.t - fc.t).toFixed(2)} s`,
                guideDelta(fc, data.guideReports.find(g => g.captures.includes(nearestFrame.capture)) ?? null, fmtW),
                <LabelLines labels={labelsOf(fc)} catchToggle={{ captureId: fc.id, options: catchOptions, onChange: setLabels }} />,
              ]}
            />
          )}
          <InfoBox tone="cursor"
            title={<>
              At cursor · {fmtClock(curT * 1000)}
              {nearestFrame && Math.abs(nearestFrame.t - curT) > 1 && (
                <span className="normal-case tracking-normal text-stone-400"> nearest frame {(nearestFrame.t - curT).toFixed(1)} s away</span>
              )}
            </>}
            big={<span>{dense ? fmtW(dense.weight) : sp ? fmtW(sp.weight) : '—'}</span>}
            lines={[
              dense
                ? <>
                    {dense.used && <span className="text-[#2e9d4b]">used for reported weight</span>}
                    {dense.used && dense.outlier && <span className="text-stone-400"> · </span>}
                    {dense.outlier && <span className="text-[#e06666]">live outlier flag</span>}
                    {!dense.used && !dense.outlier && <span className="text-stone-500">reading during capture</span>}
                  </>
                : sp ? `5 s weight log, ${Math.abs(sp.t - curT).toFixed(1)} s ${sp.t > curT ? 'after' : 'before'} cursor`
                : 'no reading within 2.6 s',
              (dense ?? sp)
                ? channels.map(ch => {
                    const v = (dense ?? sp)!.raw[ch]
                    return `raw ch${ch} ${v ?? '—'}${isRailed(v) ? ' (ADC railed)' : ''}`
                  }).join(' · ')
                : null,
            ]}
          />
          <div className="mt-2 text-[10.5px] text-stone-400 leading-snug max-w-[340px]">
            hover to spotlight · click to pin · hold ←/→ to move through time (Shift = slow) · [ / ] previous / next burst
          </div>
        </div>
      </div>

      <Legend units={units} />

      {/* strip with a fixed y-axis column */}
      <div className="flex mx-5 mb-4 border border-stone-200 rounded-lg overflow-hidden">
        <div className="relative flex-none border-r border-stone-200 bg-white" style={{ width: LABEL_W, height: totalH }}>
          {lanes.map(([name, h]) => {
            const scaled = name === 'weight' || name.startsWith('raw:')
            const text = name.startsWith('raw:') ? `raw ch${name.slice(4)}` : laneLabels[name]
            // scaled lanes carry tick labels on the right, so their name runs up the left edge
            return scaled ? (
              <div key={name} className="absolute left-0 flex items-center justify-center text-[11px] font-semibold text-stone-500"
                style={{ top: laneY[name][0], height: h, width: 18 }}>
                <span style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', whiteSpace: 'nowrap' }}>{text}</span>
              </div>
            ) : (
              <div key={name} className="absolute left-0 px-1.5 py-0.5 text-[11px] font-semibold text-stone-500" style={{ top: laneY[name][0], height: h }}>
                {text}
              </div>
            )
          })}
          {Object.entries(scales).flatMap(([lane, s]) => s.ticks.map(([ty, label]) => (
            <div key={`${lane}-${ty}`} className="absolute right-1.5 text-[10px] text-stone-500 tabular-nums" style={{ top: ty - 6 }}>
              {label} –
            </div>
          )))}
        </div>
        <div ref={scrollRef} className="flex-1 min-w-0 overflow-x-auto">
          <div className="relative cursor-crosshair" style={{ width: layout.width, height: totalH }} onMouseMove={onMove} onClick={onClick}>
            <svg width={layout.width} height={totalH} className="absolute left-0 top-0">{svg}</svg>
            {divs}
            {cursorX != null && (
              <div className="absolute top-0 pointer-events-none" style={{ left: cursorX, height: totalH, borderLeft: '2px solid #e0a000', zIndex: 9 }} />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

const fmtIn = (mm: number) => `${(mm / 25.4).toFixed(1)} in`

function delta(device: number | null, guide: number | null, fmt: (v: number) => string): string | null {
  if (device == null || guide == null || guide === 0) return null
  const d = device - guide
  return `${d >= 0 ? '+' : '−'}${fmt(Math.abs(d))} (${d >= 0 ? '+' : '−'}${Math.abs((d / guide) * 100).toFixed(0)}%)`
}

function TriageMark({ triage }: { triage: string }) {
  const t = TRIAGE_STYLE[triage]
  if (!t) return null
  return <span className="mr-1 font-bold" style={{ color: t.color }} title={t.label}>{t.mark}</span>
}

function TriagePill({ triage }: { triage: string }) {
  const t = TRIAGE_STYLE[triage]
  if (!t) return null
  return <span className={`ml-2 rounded px-1.5 py-px text-[10px] tracking-[0.12em] ${t.cls}`}>{t.label}</span>
}

// The admin-defined length and weight labels switched on for a catch, by kind and category.
// Catch labels are switched on and off right here; length and weight labels are
// shown read-only (they're set in the side panel's debug views).
function CatchToggles({ captureId, applied, options, onChange }: {
  captureId: string
  applied: CaptureLabelSchema[]
  options: CaptureLabelSchema[] | null
  onChange: (captureId: string, labels: CaptureLabelSchema[]) => void
}) {
  const [error, setError] = useState<string | null>(null)
  if (options === null) return <span className="text-stone-400">…</span>
  if (options.length === 0) return <span className="text-stone-400">none defined (Settings)</span>
  async function toggle(label: CaptureLabelSchema) {
    const on = !applied.some(a => a.id === label.id)
    const prev = applied
    onChange(captureId, on ? [...applied, label] : applied.filter(a => a.id !== label.id))
    setError(null)
    try {
      const r = await Captures.setCaptureLabel({ setCaptureLabelRequest: { captureIds: [captureId], labelId: label.id, applied: on } })
      onChange(captureId, r.captures[0]?.labels ?? [])
    } catch (e) {
      onChange(captureId, prev)
      setError(String(e))
    }
  }
  return (
    <>
      {groupByCategory(options).flatMap(([category, ls]) => ls.map(l => {
        const on = applied.some(a => a.id === l.id)
        return (
          <button key={l.id} type="button" onClick={() => toggle(l)} aria-pressed={on} title={l.description ?? undefined}
            className={`rounded border px-1.5 text-[11px] transition-colors ${on
              ? 'bg-coral/10 text-coral border-coral/40'
              : 'bg-white text-stone-400 border-stone-200 hover:border-stone-400 hover:text-stone-600'}`}>
            {on ? '✓ ' : ''}{category && <span className="opacity-70">{category}: </span>}{l.name}
          </button>
        )
      }))}
      {error && <span className="text-rose-700 text-[11px]">{error}</span>}
    </>
  )
}

function LabelLines({ labels, catchToggle }: {
  labels: CaptureLabelSchema[]
  catchToggle: { captureId: string; options: CaptureLabelSchema[] | null; onChange: (captureId: string, labels: CaptureLabelSchema[]) => void }
}) {
  return (
    <div className="mt-1 space-y-0.5">
      {(['catch', 'length', 'weight'] as const).map(kind => {
        const ofKind = labels.filter(l => l.kind === kind)
        if (kind === 'catch') {
          return (
            <div key={kind} className="flex flex-wrap items-baseline gap-1">
              <span className="w-[52px] flex-none text-[10px] uppercase tracking-[0.12em] text-stone-400">{kind}</span>
              <CatchToggles captureId={catchToggle.captureId} applied={labels.filter(l => l.kind === 'catch')}
                options={catchToggle.options} onChange={catchToggle.onChange} />
            </div>
          )
        }
        return (
          <div key={kind} className="flex flex-wrap items-baseline gap-1">
            <span className="w-[52px] flex-none text-[10px] uppercase tracking-[0.12em] text-stone-400">{kind}</span>
            {ofKind.length === 0
              ? <span className="text-stone-400">no labels</span>
              : groupByCategory(ofKind).flatMap(([category, ls]) => ls.map(l => (
                  <span key={l.id} title={l.description ?? undefined}
                    className="rounded border border-ink-200/60 bg-white px-1.5 text-[11px] text-ink-700">
                    {category && <span className="text-stone-400">{category}: </span>}{l.name}
                  </span>
                )))}
          </div>
        )
      })}
    </div>
  )
}

// Compare a catch only with the guide report for its own fish (a burst can hold
// two fish caught minutes apart); an untagged catch gets no comparison.
function guideDelta(c: BurstCapture, g: GuideReport | null, fmtW: (g: number) => string): string | null {
  if (!g) return null
  const parts = [delta(c.lengthMm, g.lengthMm, fmtIn), delta(c.weightG, g.weightG, fmtW)].filter(Boolean)
  return parts.length ? `vs guide ${parts.join(', ')}` : null
}

// A metadata box: small caps title, big numbers, a few detail lines.
function InfoBox({ tone, title, big, lines }: {
  tone: 'device' | 'cursor'
  title: React.ReactNode
  big: React.ReactNode
  lines: React.ReactNode[]
}) {
  const cls = tone === 'device' ? 'border-ink-200 bg-stone-50 text-ink-500' : 'border-[#e0a000]/60 bg-[#e0a000]/5 text-[#9a6d00]'
  return (
    <div className={`mb-3 rounded-lg border-2 px-3 py-2 ${cls}`}>
      <div className="text-[10px] uppercase tracking-[0.16em] font-semibold">{title}</div>
      <div className="flex items-baseline gap-5 font-display font-extrabold text-ink-700 text-[26px] leading-tight">{big}</div>
      {lines.filter(l => l != null && l !== '').map((l, i) => (
        <div key={i} className="text-[11.5px] text-ink-500">{l}</div>
      ))}
    </div>
  )
}

// The guide estimate for the spotlighted catch's fish (normally one card).
function GuideCard({ reports, captures, fmtW, onOpenCatch }: {
  reports: GuideReport[]
  captures: BurstCapture[]
  fmtW: (g: number) => string
  onOpenCatch: (captureId: string) => void
}) {
  if (reports.length === 0) {
    return (
      <div className="mb-3 rounded-lg border border-dashed border-stone-300 px-3 py-2 text-[11px] text-stone-400">
        no guide estimate for this catch
      </div>
    )
  }
  return (
    <div>
      {reports.map(r => {
        return (
          <div key={r.captures[0]}
            className="mb-3 rounded-lg border-2 border-pine/60 bg-pine/5 px-3 py-2">
            <div className="flex items-baseline gap-2 text-[10px] uppercase tracking-[0.16em] text-pine font-semibold">
              <span>Guide reported{r.guides.length > 1 ? ` (mean of ${r.guides.length})` : ''}</span>
              {r.label && <span className="text-stone-400 normal-case tracking-normal">{r.label}</span>}
              {r.angler && <span className="text-stone-500 normal-case tracking-normal">angler {r.angler}</span>}
              <button className="text-stone-400 normal-case tracking-normal hover:text-bear" onClick={() => onOpenCatch(captures[r.captures[0]].id)}
                title="the captures this was entered on">
                {r.captures.length === captures.length && captures.length > 1
                  ? `on all ${captures.length} triggers`
                  : `on ${r.captures.map(i => `#${i + 1}`).join(', ')}`}
              </button>
            </div>
            <div className="flex items-baseline gap-5 font-display font-extrabold text-ink-700">
              <span className="text-[26px] leading-tight">{r.lengthMm != null ? fmtIn(r.lengthMm) : '—'}</span>
              <span className="text-[26px] leading-tight">{r.weightG != null ? fmtW(r.weightG) : '—'}</span>
            </div>
            {r.guides.length > 0 && (
              <div className="text-[11.5px] text-ink-500">
                {r.guides.map(g => `${g.name} ${g.lengthIn ?? '?'} in / ${g.weightLb ?? '?'} lb`).join(' · ')}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

function SpotImage({ url, chosen }: { url: string | null; chosen: boolean }) {
  return (
    <div className="flex-none rounded-lg overflow-hidden bg-stone-200" style={{ width: 400, height: 300, border: `3px solid ${chosen ? '#e0a000' : '#fff'}` }}>
      {url && <img src={url} alt="" className="w-full h-full object-cover" draggable={false} />}
    </div>
  )
}

function Legend({ units }: { units: 'g' | 'lb' }) {
  const item = 'flex items-center gap-1 whitespace-nowrap'
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 px-5 pb-2 text-[10.5px] text-stone-500">
      <span className={item}><svg width="18" height="8"><line x1="0" y1="4" x2="18" y2="4" stroke="#222" strokeWidth="1.3" /><circle cx="9" cy="4" r="2.2" fill="#222" /></svg>weight ({units}), every reading</span>
      <span className={item} style={{ color: '#2e9d4b' }}><svg width="10" height="10"><circle cx="5" cy="5" r="4.2" fill="#2e9d4b" /></svg>+ green band: the readings the reported weight is the median of</span>
      <span className={item} style={{ color: '#e06666' }}>✕ live outlier flag (doesn't affect the reported weight)</span>
      <span className={item} style={{ color: '#7a6fa0' }}>━ in gaps: weight logged every 5 s</span>
      <span className={item} style={{ color: TARE_COLOR, fontWeight: 700 }}>┃ tare</span>
      <span className={item}><span style={{ border: '2px solid #e0a000', width: 10, height: 8, display: 'inline-block' }} />device's chosen frame</span>
    </div>
  )
}
